import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useFormik } from "formik";
import { StrategyName } from "../../component/Schemas";
import { useSelector } from "react-redux";
import { selectStrategies, selectManagers } from "../../Redux/Reducers/selectors";

const StrategiesDialog = ({ open, onClose, strategy, onSave }) => {
 
  const managers = useSelector(selectManagers);
  const strategies = useSelector(selectStrategies);
  const [formats, setFormats] = useState(["personal"]);

  const isEdit = !!strategy;

  useEffect(() => {
    if (isEdit && strategy) {
      // Set initial format based on strategy owner
      if (strategy.owner?.includes("manager#")) {
        setFormats(["manager"]);
      } else {
        setFormats(["personal"]);
      }
    } else {
      setFormats(["personal"]);
    }
  }, [strategy, isEdit]);

  const handleFormat = (event, newFormat) => {
    if (newFormat !== null) {
      setFormats([newFormat]);
    }
  };

  const initialValues = {
    name: strategy?.name || "",
    description: strategy?.description || "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: StrategyName,
    enableReinitialize: true,
    onSubmit: (values) => {
      const duplicateStrategy = strategies?.find(
        (s) =>
          s.name.toLowerCase() === values.name.toLowerCase() &&
          (!isEdit || s.strategyId !== strategy.strategyId)
      );

      if (duplicateStrategy) {
        alert(`${values.name} already exists, try a new one.`);
        return;
      }

      const requestData = { ...values };

      // Only add owner if manager format is selected
      if (formats[0] === "manager") {
        requestData.owner = `manager#${managers[0]?.managerId}`;
      }

      onSave(requestData);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setFormats(["personal"]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          pt: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 48,
                height: 48,
              }}
            >
              <TrendingUpIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: "text.primary",

                }}
              >
                {isEdit ? "Edit Strategy" : "Add Strategy"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                {isEdit
                  ? "Update strategy information and settings"
                  : "Create a new trading strategy with custom parameters"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ overflowY: "visible" }}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Strategy Name"
              name="name"
              value={formik.values.name}
              disabled={isEdit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              InputLabelProps={{
                shrink: true,
                sx: { fontWeight: 600 },
              }}
              sx={{ mt: 1 }}
            />

            <TextField
              fullWidth
              variant="outlined"
              label="Description"
              name="description"
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              InputLabelProps={{
                shrink: true,
                sx: { fontWeight: 600 },
              }}
            />

            {/* Strategy Type Selection */}
            <FormControl fullWidth>
              <InputLabel shrink sx={{ fontWeight: 600, mb: 1 }}>
                Strategy Owner
              </InputLabel>
              <ToggleButtonGroup
                value={formats[0]}
                exclusive
                onChange={handleFormat}
                aria-label="strategy type"
                sx={{
                  mt: 2,
                  "& .MuiToggleButton-root": {
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                  },
                }}
              >
                <ToggleButton value="personal" aria-label="personal">
                  Personal
                </ToggleButton>
                <ToggleButton value="manager" aria-label="manager">
                  Manager
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            {/* Action Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 120,
                }}
              >
                {isEdit ? "Save" : "Create Strategy"}
              </Button>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StrategiesDialog;
